import {store} from '../../store/store';
import {EventType, sendMatrixEvent} from '../../store/features/MatrixEvents/MatrixEventsService';
import logger from '../logger';

export interface CallAccountingContext {
    readonly tenant: string;
    readonly eventOwner: number;
    readonly metadata: string;
    readonly callId: string;
    readonly matrixCallId: string;
    readonly roomId: string;
    readonly clientSessionId: string;
}

interface PendingTerminal {
    readonly context: CallAccountingContext;
    readonly type: EventType.HANGUP | EventType.REJECT;
    readonly occurredAt: number;
}

const prefix = 'phonehq:call-accounting:v1:';
let delivery: Promise<void> | undefined;

export function persistCallTermination(record: PendingTerminal): void {
    const {tenant, eventOwner, matrixCallId} = record.context;
    const key = prefix + JSON.stringify([tenant, eventOwner, matrixCallId, record.type]);
    // One storage entry per event prevents concurrent browser tabs overwriting a shared queue.
    if (!localStorage.getItem(key)) localStorage.setItem(key, JSON.stringify(record));
}

export function flushCallAccounting(): Promise<void> {
    if (delivery) return delivery;
    delivery = deliverPending().catch(() => {
        logger.warn('Matrix accounting storage is unavailable', {source: 'matrix_call', event: 'accounting_storage_failed'});
    }).finally(() => { delivery = undefined; });
    return delivery;
}

async function deliverPending(): Promise<void> {
    const keys = Object.keys(localStorage).filter(key => key.startsWith(prefix));
    for (const key of keys) {
        try {
            const serialized = localStorage.getItem(key);
            if (!serialized) continue;
            const record: PendingTerminal = JSON.parse(serialized);
            const {context, type, occurredAt} = record;
            const auth = store.getState().auth;
            if (auth.tenant !== context.tenant || Number(auth.userId) !== context.eventOwner) continue;
            await sendMatrixEvent({
                type, occurredAt, metadata: context.metadata, eventOwner: context.eventOwner,
                roomId: context.roomId, matrixCallId: context.matrixCallId, clientSessionId: context.clientSessionId,
            });
            localStorage.removeItem(key);
        } catch {
            logger.warn('Matrix terminal accounting remains queued', {source: 'matrix_call', event: 'accounting_retry'});
        }
    }
}
